import { useCallback } from 'react';
import {
  Plug,
  Globe,
  Key,
  Terminal,
  Monitor,
  Code2,
  MessageSquare,
  Sparkles,
  Shield,
  HelpCircle,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Zap,
  Search,
  Mail,
  Trash2,
  BarChart3,
  Users,
  FileEdit,
  Eye,
  Plus,
  Camera,
  Play,
} from 'lucide-react';
import { CodeBlock } from '@/components/academy/CodeBlock';
import { CopyPageButton } from '@/components/academy/CopyPageButton';

/* ════════════════════════════════════════════════════════════════
   Data
   ════════════════════════════════════════════════════════════════ */

const sections = [
  { id: 'overview', label: 'Overview' },
  { id: 'claude-web', label: 'Claude.ai (Web)' },
  { id: 'claude-desktop', label: 'Claude Desktop' },
  { id: 'claude-code', label: 'Claude Code (CLI)' },
  { id: 'chatgpt', label: 'ChatGPT' },
  { id: 'cursor-vscode', label: 'Cursor / VS Code' },
  { id: 'other-platforms', label: 'Other Platforms' },
  { id: 'tools-reference', label: 'Tools Reference' },
  { id: 'troubleshooting', label: 'Troubleshooting' },
];

const tools = [
  {
    name: 'research_company',
    desc: 'Research a company by URL using AI web search. Returns industry, product, value proposition, leadership, and target audience.',
    icon: Search,
    type: 'AI-powered',
    readOnly: true,
    cost: '~25\u201340 tokens',
  },
  {
    name: 'generate_leads',
    desc: 'Find real prospect leads by searching the web for people matching your criteria (industry, region, role, company size).',
    icon: Users,
    type: 'AI-powered',
    readOnly: false,
    cost: '~30\u201360 tokens',
  },
  {
    name: 'list_leads',
    desc: 'List all leads with optional filtering by status, search, and pagination.',
    icon: Eye,
    type: 'CRUD',
    readOnly: true,
    cost: 'Free',
  },
  {
    name: 'get_lead',
    desc: 'Get full details of a specific lead by ID.',
    icon: Eye,
    type: 'CRUD',
    readOnly: true,
    cost: 'Free',
  },
  {
    name: 'create_lead',
    desc: 'Create a new lead manually with email, name, company, role, and notes.',
    icon: Plus,
    type: 'CRUD',
    readOnly: false,
    cost: 'Free',
  },
  {
    name: 'update_lead',
    desc: 'Update an existing lead\u2019s information (name, company, role, status, notes).',
    icon: FileEdit,
    type: 'CRUD',
    readOnly: false,
    cost: 'Free',
  },
  {
    name: 'delete_lead',
    desc: 'Permanently delete a lead. This action cannot be undone.',
    icon: Trash2,
    type: 'CRUD',
    readOnly: false,
    cost: 'Free',
    destructive: true,
  },
  {
    name: 'generate_email',
    desc: 'Generate a hyper-personalized cold email for a specific lead using AI.',
    icon: Mail,
    type: 'AI-powered',
    readOnly: false,
    cost: '~15\u201325 tokens',
  },
  {
    name: 'generate_email_batch',
    desc: 'Generate personalized emails for all leads in a group at once.',
    icon: Mail,
    type: 'AI-powered',
    readOnly: false,
    cost: '~15\u201325 tokens/lead',
  },
  {
    name: 'check_usage',
    desc: 'Check your remaining HyPeMa token balance, usage, and plan limit.',
    icon: BarChart3,
    type: 'Utility',
    readOnly: true,
    cost: 'Free',
  },
];

const faqItems = [
  {
    q: 'OAuth login opens but nothing happens after I authorize',
    a: 'Make sure third-party cookies are enabled for the OAuth callback. In some browsers, the popup may be blocked \u2014 check your browser\u2019s popup blocker settings. Try again in a new tab.',
  },
  {
    q: 'I get "Invalid or expired OAuth token"',
    a: 'Your session has expired. Disconnect and reconnect the HyPeMa connector in your AI platform\u2019s settings. This will trigger a fresh OAuth login.',
  },
  {
    q: '"Authentication required" on every tool call',
    a: 'Your API key or OAuth token isn\u2019t being sent. For API key setups (Cursor, VS Code), make sure the Authorization header is set to "Bearer hpm_live_..." in your MCP config.',
  },
  {
    q: 'Tools appear but return errors',
    a: 'Check your token balance with check_usage. If you\u2019re out of tokens, upgrade your plan at Settings \u2192 Billing. AI operations (research, lead generation, email) consume tokens.',
  },
  {
    q: 'Can I use both the web app and MCP at the same time?',
    a: 'Yes! They share the same data and token pool. Leads created via MCP appear in the web app and vice versa.',
  },
  {
    q: 'Which AI platforms support MCP?',
    a: 'Claude.ai, Claude Desktop, Claude Code, ChatGPT (Developer Mode), Cursor, VS Code, OpenClaw, Windsurf, JetBrains IDEs, Zed, Cline, Goose, and Google Gemini (ADK). Any MCP-compatible client works.',
  },
];

/* ════════════════════════════════════════════════════════════════
   Page Component
   ════════════════════════════════════════════════════════════════ */

export function AcademyMcpPage() {
  const smoothScroll = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      const id = e.currentTarget.getAttribute('href')?.replace('#', '');
      if (!id) return;
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [],
  );

  return (
    <div className="animate-fade-in flex gap-8">
      {/* Sticky ToC */}
      <aside className="hidden lg:block w-48 shrink-0">
        <nav className="sticky top-6 space-y-1">
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={smoothScroll}
              className="block text-sm text-base-content/50 hover:text-base-content py-1 transition-colors"
            >
              {s.label}
            </a>
          ))}
        </nav>
      </aside>

      <div id="mcp-content" className="flex-1 min-w-0 space-y-12 max-w-3xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">MCP Integration Guide</h1>
            <p className="text-sm text-base-content/40 mt-1">
              Connect HyPeMa to Claude, ChatGPT, Cursor, and any AI platform that supports MCP.
            </p>
          </div>
          <CopyPageButton contentSelector="#mcp-content" title="HyPeMa MCP Integration Guide" />
        </div>

        {/* ──────────────────────────────────────────────────────────
            1. Overview
           ────────────────────────────────────────────────────────── */}
        <section id="overview" className="scroll-mt-6 space-y-5">
          <SectionHeading icon={Plug} title="Overview" />

          <div className="glass-card rounded-xl p-5 space-y-5">
            <p className="text-sm text-base-content/70 leading-relaxed">
              HyPeMa has a remote <strong>MCP server</strong> (Model Context Protocol) that lets
              any compatible AI assistant use HyPeMa directly. Instead of switching to the web
              app, you can research companies, find leads, and draft emails from inside your AI
              chat.
            </p>

            <div className="glass-card rounded-xl p-4 border-l-4 border-primary/60">
              <p className="text-sm font-medium text-primary/90">One server, every platform</p>
              <p className="text-sm text-base-content/60 mt-1">
                The same MCP endpoint works with Claude.ai, Claude Desktop, ChatGPT, Cursor,
                VS Code, and 10+ other AI platforms. Set it up once and your AI assistant gets
                access to all 10 HyPeMa tools.
              </p>
            </div>

            {/* Architecture */}
            <McpArchitectureDiagram />

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                Two ways to authenticate
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <AuthMethodCard
                  icon={Globe}
                  title="OAuth (recommended)"
                  desc="For Claude.ai, ChatGPT, Claude Desktop. Sign in with your HyPeMa or Google account. Auto-configured during setup."
                />
                <AuthMethodCard
                  icon={Key}
                  title="API Key"
                  desc="For Cursor, VS Code, CLI tools. Generate a key at Settings \u2192 API Keys and add it to your config file."
                />
              </div>
            </div>

            <div className="rounded-lg bg-base-200/50 p-3">
              <p className="text-xs text-base-content/50">
                <span className="font-semibold">Server URL:</span>{' '}
                <code className="text-xs bg-base-300/50 px-1.5 py-0.5 rounded">
                  https://mcp.hypema.app/mcp
                </code>
              </p>
            </div>
          </div>
        </section>

        {/* ──────────────────────────────────────────────────────────
            2. Claude.ai (Web)
           ────────────────────────────────────────────────────────── */}
        <section id="claude-web" className="scroll-mt-6 space-y-5">
          <SectionHeading icon={MessageSquare} title="Claude.ai (Web & Mobile)" />

          <div className="glass-card rounded-xl p-5 space-y-4">
            <p className="text-sm text-base-content/70 leading-relaxed">
              The easiest way to get started. Claude.ai has built-in support for MCP connectors
              with OAuth &mdash; just add the URL and sign in.
            </p>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                Setup steps
              </p>
              <ol className="space-y-2 text-sm text-base-content/70">
                <StepItem number={1}>
                  Open <strong>Claude.ai</strong> and go to{' '}
                  <span className="font-medium">Settings &rarr; Connectors</span>
                </StepItem>
                <StepItem number={2}>
                  Click <span className="font-medium">&ldquo;Add custom connector&rdquo;</span>
                </StepItem>
                <StepItem number={3}>
                  Enter the URL:{' '}
                  <code className="text-xs bg-base-300/50 px-1.5 py-0.5 rounded">
                    https://mcp.hypema.app/mcp
                  </code>
                </StepItem>
                <StepItem number={4}>
                  Claude opens the HyPeMa login page &mdash; sign in with your HyPeMa or
                  Google account and approve access
                </StepItem>
                <StepItem number={5}>
                  Done! HyPeMa tools now appear in your Claude conversations. Try:{' '}
                  <span className="italic text-base-content/50">
                    &ldquo;Research the company stripe.com&rdquo;
                  </span>
                </StepItem>
              </ol>
            </div>

            <div className="glass-card rounded-xl p-4 border-l-4 border-warning/60">
              <p className="text-sm font-medium text-warning/90">Note</p>
              <p className="text-sm text-base-content/60 mt-1">
                Free Claude users can use published connectors from the directory. Custom
                connectors (like adding HyPeMa by URL) require a paid Claude plan (Pro, Max,
                Team, or Enterprise).
              </p>
            </div>

            <ScreenshotPlaceholder caption="Claude.ai Settings \u2192 Connectors \u2192 Add custom connector" />
          </div>
        </section>

        {/* ──────────────────────────────────────────────────────────
            3. Claude Desktop
           ────────────────────────────────────────────────────────── */}
        <section id="claude-desktop" className="scroll-mt-6 space-y-5">
          <SectionHeading icon={Monitor} title="Claude Desktop" />

          <div className="glass-card rounded-xl p-5 space-y-4">
            <p className="text-sm text-base-content/70 leading-relaxed">
              Claude Desktop supports MCP connectors through its GUI or via a JSON config file.
              You can use either OAuth (same as web) or an API key.
            </p>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                Option A: Via GUI (OAuth)
              </p>
              <ol className="space-y-2 text-sm text-base-content/70">
                <StepItem number={1}>
                  Open Claude Desktop and go to{' '}
                  <span className="font-medium">Settings &rarr; Connectors</span>
                </StepItem>
                <StepItem number={2}>
                  Click <span className="font-medium">&ldquo;Add custom connector&rdquo;</span> and
                  enter{' '}
                  <code className="text-xs bg-base-300/50 px-1.5 py-0.5 rounded">
                    https://mcp.hypema.app/mcp
                  </code>
                </StepItem>
                <StepItem number={3}>
                  Sign in with your HyPeMa account when prompted
                </StepItem>
              </ol>
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                Option B: Via config file (API Key)
              </p>
              <p className="text-xs text-base-content/50">
                Edit{' '}
                <code className="bg-base-300/50 px-1 py-0.5 rounded">
                  claude_desktop_config.json
                </code>{' '}
                (Settings &rarr; Developer &rarr; Edit Config):
              </p>
              <CodeBlock
                language="json"
                code={`{
  "mcpServers": {
    "hypema": {
      "url": "https://mcp.hypema.app/mcp",
      "headers": {
        "Authorization": "Bearer hpm_live_your_key_here"
      }
    }
  }
}`}
              />
              <p className="text-xs text-base-content/40">
                Get your API key at{' '}
                <span className="font-medium">Settings &rarr; API Keys</span> in the HyPeMa web
                app.
              </p>
            </div>
          </div>
        </section>

        {/* ──────────────────────────────────────────────────────────
            4. Claude Code (CLI)
           ────────────────────────────────────────────────────────── */}
        <section id="claude-code" className="scroll-mt-6 space-y-5">
          <SectionHeading icon={Terminal} title="Claude Code (CLI)" />

          <div className="glass-card rounded-xl p-5 space-y-4">
            <p className="text-sm text-base-content/70 leading-relaxed">
              Claude Code is Anthropic&apos;s agentic coding tool that runs in your terminal.
              It supports remote MCP servers natively &mdash; just add the URL and Claude Code
              handles OAuth automatically.
            </p>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                Option A: Add via CLI command
              </p>
              <CodeBlock
                language="bash"
                code="claude mcp add hypema --transport http https://mcp.hypema.app/mcp"
              />
              <p className="text-xs text-base-content/50">
                Claude Code opens a browser for OAuth login automatically on first use.
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                Option B: Add with API key (skip OAuth)
              </p>
              <CodeBlock
                language="bash"
                code={`claude mcp add hypema \\
  --transport http \\
  --header "Authorization: Bearer hpm_live_your_key_here" \\
  https://mcp.hypema.app/mcp`}
              />
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                Option C: Edit config file directly
              </p>
              <p className="text-xs text-base-content/50">
                Add to{' '}
                <code className="bg-base-300/50 px-1 py-0.5 rounded">
                  ~/.claude/settings.json
                </code>
                :
              </p>
              <CodeBlock
                language="json"
                code={`{
  "mcpServers": {
    "hypema": {
      "type": "http",
      "url": "https://mcp.hypema.app/mcp",
      "headers": {
        "Authorization": "Bearer hpm_live_your_key_here"
      }
    }
  }
}`}
              />
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                Verify connection
              </p>
              <CodeBlock language="bash" code="claude mcp list" />
              <p className="text-xs text-base-content/50">
                You should see <code className="bg-base-300/50 px-1 py-0.5 rounded">hypema</code>{' '}
                listed with 10 tools. Try it out:
              </p>
              <CodeBlock
                language="bash"
                code={`claude
> Research the company stripe.com and find 3 VP-level leads in partnerships`}
              />
            </div>

            <div className="glass-card rounded-xl p-4 border-l-4 border-info/60">
              <p className="text-sm font-medium text-info/90">Scopes</p>
              <p className="text-sm text-base-content/60 mt-1">
                You can add HyPeMa at different scopes:{' '}
                <code className="text-xs bg-base-300/50 px-1 py-0.5 rounded">--scope user</code>{' '}
                (all projects),{' '}
                <code className="text-xs bg-base-300/50 px-1 py-0.5 rounded">--scope project</code>{' '}
                (current project only), or{' '}
                <code className="text-xs bg-base-300/50 px-1 py-0.5 rounded">--scope local</code>{' '}
                (local machine only).
              </p>
            </div>
          </div>
        </section>

        {/* ──────────────────────────────────────────────────────────
            5. ChatGPT
           ────────────────────────────────────────────────────────── */}
        <section id="chatgpt" className="scroll-mt-6 space-y-5">
          <SectionHeading icon={MessageSquare} title="ChatGPT" />

          <div className="glass-card rounded-xl p-5 space-y-4">
            <p className="text-sm text-base-content/70 leading-relaxed">
              ChatGPT supports MCP connectors via Developer Mode. OAuth is required &mdash;
              API keys are not supported for ChatGPT connectors.
            </p>

            <div className="glass-card rounded-xl p-4 border-l-4 border-warning/60">
              <p className="text-sm font-medium text-warning/90">Requirement</p>
              <p className="text-sm text-base-content/60 mt-1">
                Developer Mode must be enabled (available on Pro, Team, Enterprise, and Edu
                plans). Go to Settings &rarr; Developer Mode to enable it.
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                Setup steps
              </p>
              <ol className="space-y-2 text-sm text-base-content/70">
                <StepItem number={1}>
                  Open <strong>ChatGPT</strong> and go to{' '}
                  <span className="font-medium">Settings &rarr; Apps &amp; Connectors</span>
                </StepItem>
                <StepItem number={2}>
                  Click <span className="font-medium">&ldquo;Add new connector&rdquo;</span>
                </StepItem>
                <StepItem number={3}>
                  Enter name: <span className="font-medium">HyPeMa</span> and URL:{' '}
                  <code className="text-xs bg-base-300/50 px-1.5 py-0.5 rounded">
                    https://mcp.hypema.app/mcp
                  </code>
                </StepItem>
                <StepItem number={4}>
                  ChatGPT opens the HyPeMa login page &mdash; sign in and approve
                </StepItem>
                <StepItem number={5}>
                  Enable HyPeMa in your chat via the connectors toggle
                </StepItem>
              </ol>
            </div>
          </div>
        </section>

        {/* ──────────────────────────────────────────────────────────
            6. Cursor / VS Code
           ────────────────────────────────────────────────────────── */}
        <section id="cursor-vscode" className="scroll-mt-6 space-y-5">
          <SectionHeading icon={Code2} title="Cursor / VS Code / GitHub Copilot" />

          <div className="glass-card rounded-xl p-5 space-y-4">
            <p className="text-sm text-base-content/70 leading-relaxed">
              IDE-based AI tools support MCP via config files. These use API key authentication
              (no OAuth popup).
            </p>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                Cursor
              </p>
              <p className="text-xs text-base-content/50">
                Create or edit{' '}
                <code className="bg-base-300/50 px-1 py-0.5 rounded">.cursor/mcp.json</code> in
                your project root:
              </p>
              <CodeBlock
                language="json"
                code={`{
  "mcpServers": {
    "hypema": {
      "url": "https://mcp.hypema.app/mcp",
      "headers": {
        "Authorization": "Bearer hpm_live_your_key_here"
      }
    }
  }
}`}
              />
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                VS Code / GitHub Copilot
              </p>
              <p className="text-xs text-base-content/50">
                Open Settings (JSON) and add:
              </p>
              <CodeBlock
                language="json"
                code={`{
  "mcp": {
    "servers": {
      "hypema": {
        "type": "http",
        "url": "https://mcp.hypema.app/mcp",
        "headers": {
          "Authorization": "Bearer hpm_live_your_key_here"
        }
      }
    }
  }
}`}
              />
            </div>

            <p className="text-xs text-base-content/40">
              Get your API key at{' '}
              <span className="font-medium">Settings &rarr; API Keys</span> in the HyPeMa web
              app. Keys start with{' '}
              <code className="bg-base-300/50 px-1 py-0.5 rounded">hpm_live_</code>.
            </p>
          </div>
        </section>

        {/* ──────────────────────────────────────────────────────────
            7. Other Platforms
           ────────────────────────────────────────────────────────── */}
        <section id="other-platforms" className="scroll-mt-6 space-y-5">
          <SectionHeading icon={Globe} title="Other Platforms" />

          <div className="glass-card rounded-xl p-5 space-y-4">
            <p className="text-sm text-base-content/70 leading-relaxed">
              HyPeMa works with any MCP-compatible client. Here are configs for additional
              platforms.
            </p>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                OpenClaw
              </p>
              <CodeBlock
                language="bash"
                code="openclaw mcp add hypema --url https://mcp.hypema.app/mcp"
              />
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                Windsurf (Codeium)
              </p>
              <CodeBlock
                language="json"
                code={`{
  "mcpServers": {
    "hypema": {
      "serverUrl": "https://mcp.hypema.app/mcp",
      "headers": {
        "Authorization": "Bearer hpm_live_your_key_here"
      }
    }
  }
}`}
              />
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                OpenAI Agents SDK (Python)
              </p>
              <CodeBlock
                language="python"
                code={`from openai import OpenAI
client = OpenAI()

response = client.responses.create(
    model="gpt-4o",
    tools=[{
        "type": "mcp",
        "server_url": "https://mcp.hypema.app/mcp",
        "authorization": {
            "token": "Bearer <your_oauth_token>"
        }
    }],
    input="Find 5 CTOs at fintech companies in Germany"
)`}
              />
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                Also supported
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {['JetBrains IDEs', 'Zed', 'Cline', 'Goose', 'Google Gemini (ADK)'].map(
                  (name) => (
                    <div
                      key={name}
                      className="rounded-lg bg-base-200/40 px-3 py-2 text-xs text-base-content/60 text-center"
                    >
                      {name}
                    </div>
                  ),
                )}
              </div>
              <p className="text-xs text-base-content/40">
                All use the same URL:{' '}
                <code className="bg-base-300/50 px-1 py-0.5 rounded">
                  https://mcp.hypema.app/mcp
                </code>{' '}
                with an API key in the Authorization header.
              </p>
            </div>
          </div>
        </section>

        {/* ──────────────────────────────────────────────────────────
            8. Tools Reference
           ────────────────────────────────────────────────────────── */}
        <section id="tools-reference" className="scroll-mt-6 space-y-5">
          <SectionHeading icon={Sparkles} title="Tools Reference" />

          <p className="text-sm text-base-content/60 leading-relaxed">
            HyPeMa exposes <strong>10 tools</strong> to your AI assistant. Each tool maps
            to a REST API endpoint and uses your HyPeMa token balance for AI operations.
          </p>

          <div className="space-y-2">
            {tools.map((tool) => (
              <ToolRow key={tool.name} {...tool} />
            ))}
          </div>

          <div className="glass-card rounded-xl p-4 border-l-4 border-info/60">
            <p className="text-sm font-medium text-info/90">Token costs</p>
            <p className="text-sm text-base-content/60 mt-1">
              CRUD operations (list, get, create, update, delete) and check_usage are free.
              AI-powered operations (research, generate leads, generate emails) consume tokens.
              100 tokens = 1 EUR. Free tier: 500 tokens/month.
            </p>
          </div>
        </section>

        {/* ──────────────────────────────────────────────────────────
            9. Troubleshooting
           ────────────────────────────────────────────────────────── */}
        <section id="troubleshooting" className="scroll-mt-6 space-y-5">
          <SectionHeading icon={HelpCircle} title="Troubleshooting" />

          <div className="glass-card rounded-xl p-5 space-y-3">
            {faqItems.map((item, i) => (
              <div key={i} className="space-y-1">
                <p className="text-sm font-medium text-base-content/80">{item.q}</p>
                <p className="text-sm text-base-content/60">{item.a}</p>
                {i < faqItems.length - 1 && <div className="border-b border-base-content/5 pt-2" />}
              </div>
            ))}
          </div>

          <div className="rounded-lg bg-base-200/50 p-3 text-center">
            <p className="text-xs text-base-content/40">
              Still stuck? Contact support at{' '}
              <span className="font-medium">support@hypema.app</span>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   Helper Components
   ════════════════════════════════════════════════════════════════ */

function SectionHeading({ icon: Icon, title }: { icon: React.ElementType; title: string }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={20} className="text-primary" />
      <h2 className="text-lg font-semibold">{title}</h2>
    </div>
  );
}

function StepItem({ number, children }: { number: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/15 text-primary text-xs font-bold shrink-0 mt-0.5">
        {number}
      </span>
      <span>{children}</span>
    </li>
  );
}

function AuthMethodCard({
  icon: Icon,
  title,
  desc,
}: {
  icon: React.ElementType;
  title: string;
  desc: string;
}) {
  return (
    <div className="rounded-lg border border-base-content/10 bg-base-100 p-4 space-y-2">
      <div className="flex items-center gap-2">
        <Icon size={16} className="text-primary" />
        <p className="text-sm font-semibold">{title}</p>
      </div>
      <p className="text-xs text-base-content/50">{desc}</p>
    </div>
  );
}

function ToolRow({
  name,
  desc,
  icon: Icon,
  type,
  readOnly,
  cost,
  destructive,
}: {
  name: string;
  desc: string;
  icon: React.ElementType;
  type: string;
  readOnly: boolean;
  cost: string;
  destructive?: boolean;
}) {
  return (
    <div className="glass-card rounded-xl p-4 flex items-start gap-3">
      <Icon
        size={16}
        className={`mt-0.5 shrink-0 ${destructive ? 'text-error/60' : 'text-primary/60'}`}
      />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <code className="text-sm font-semibold">{name}</code>
          <span
            className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
              type === 'AI-powered'
                ? 'bg-violet-500/10 text-violet-500'
                : type === 'CRUD'
                  ? 'bg-blue-500/10 text-blue-500'
                  : 'bg-emerald-500/10 text-emerald-500'
            }`}
          >
            {type}
          </span>
          {readOnly && (
            <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-base-content/5 text-base-content/40">
              read-only
            </span>
          )}
          {destructive && (
            <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-error/10 text-error">
              destructive
            </span>
          )}
        </div>
        <p className="text-xs text-base-content/60">{desc}</p>
        <p className="text-[10px] text-base-content/35">{cost}</p>
      </div>
    </div>
  );
}

function McpArchitectureDiagram() {
  const boxes = [
    { icon: MessageSquare, title: 'AI Assistant', sub: 'Claude / ChatGPT / Cursor', color: 'primary' },
    { icon: Plug, title: 'MCP Server', sub: 'mcp.hypema.app', color: 'secondary' },
    { icon: Zap, title: 'HyPeMa API', sub: 'REST + AI Services', color: 'accent' },
  ];
  const arrows = ['MCP protocol', 'REST API'];

  return (
    <div className="rounded-xl bg-base-200/30 p-5">
      <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-0">
        {boxes.map((box, i) => (
          <div key={box.title} className="flex items-center flex-1 min-w-0 w-full sm:w-auto">
            <div className="flex-1 rounded-lg border border-base-content/10 bg-base-100 p-3 text-center space-y-1">
              <box.icon size={20} className={`mx-auto text-${box.color}`} />
              <p className="text-xs font-semibold">{box.title}</p>
              <p className="text-[10px] text-base-content/40">{box.sub}</p>
            </div>
            {i < boxes.length - 1 && (
              <div className="hidden sm:flex flex-col items-center px-2 gap-0.5">
                <ArrowRight size={14} className="text-base-content/25" />
                <span className="text-[9px] text-base-content/25 whitespace-nowrap">
                  {arrows[i]}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ScreenshotPlaceholder({ caption }: { caption: string }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-base-content/10 bg-base-200/20 p-8 flex flex-col items-center justify-center gap-3 text-center">
      <Camera size={28} className="text-base-content/20" />
      <p className="text-xs text-base-content/30 max-w-sm">{caption}</p>
    </div>
  );
}
